import * as React from 'react'
import { env } from '@/lib/env'
import { cn } from '@/lib/utils'
import type { MediaRef } from './types'

export function mediaUrl(path: string): string {
  return `${env.VITE_SUPABASE_URL}/storage/v1/object/public/site-media/${path.split('/').map(encodeURIComponent).join('/')}`
}

/** Responsive image from the media library (browser-generated WebP variants). */
export function SiteImage({
  media,
  sizes = '100vw',
  className,
  priority,
  alt,
}: {
  media: MediaRef
  sizes?: string
  className?: string
  priority?: boolean
  alt?: string
}) {
  const variants = [...(media.variants ?? [])].sort((a, b) => a.w - b.w)
  const srcSet = variants.map((v) => `${mediaUrl(v.path)} ${v.w}w`).join(', ')
  const src = variants.at(-1) ? mediaUrl(variants.at(-1)!.path) : mediaUrl(media.path)
  return (
    <img
      src={src}
      srcSet={srcSet || undefined}
      sizes={srcSet ? sizes : undefined}
      alt={alt ?? media.alt ?? ''}
      width={media.width ?? undefined}
      height={media.height ?? undefined}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      fetchPriority={priority ? 'high' : undefined}
      className={cn('object-cover', className)}
    />
  )
}

/**
 * Turf illustration drawn in CSS, used until the owner uploads real photos.
 * No external images are ever hotlinked.
 */
export function TurfArt({ variant = 'day', className, children }: { variant?: 'day' | 'night'; className?: string; children?: React.ReactNode }) {
  const night = variant === 'night'
  return (
    <div
      aria-hidden="true"
      className={cn('relative overflow-hidden', className)}
      style={{
        background: night
          ? 'linear-gradient(180deg,#02060b 0%,#07140e 70%)'
          : 'linear-gradient(180deg,#b9d9ea 0%,#dcebe0 58%,#cfe4d2 100%)',
      }}
    >
      {night && (
        <>
          <div className="absolute inset-0 bg-[radial-gradient(45%_60%_at_15%_0%,rgba(255,242,205,0.55),transparent_70%),radial-gradient(45%_60%_at_85%_0%,rgba(255,242,205,0.5),transparent_70%)]" />
          <div className="absolute left-[14%] top-[9%] h-2.5 w-9 rounded-sm bg-[#fffbe8] shadow-[0_0_30px_10px_rgba(255,240,200,0.55)]" />
          <div className="absolute right-[14%] top-[9%] h-2.5 w-9 rounded-sm bg-[#fffbe8] shadow-[0_0_30px_10px_rgba(255,240,200,0.55)]" />
        </>
      )}
      <div className="absolute inset-x-0 top-0 h-[30%] opacity-40 [background:repeating-linear-gradient(90deg,rgba(255,255,255,0.18)_0_1px,transparent_1px_14px),repeating-linear-gradient(0deg,rgba(255,255,255,0.18)_0_1px,transparent_1px_14px)]" />
      <div
        className="absolute -inset-x-[30%] -bottom-[18%] h-[96%] origin-bottom [transform:perspective(700px)_rotateX(48deg)]"
        style={{
          background: night
            ? 'repeating-linear-gradient(90deg,#0f4c2b 0 6.25%,#0b3f22 6.25% 12.5%)'
            : 'repeating-linear-gradient(90deg,#3f9c50 0 6.25%,#378f48 6.25% 12.5%)',
        }}
      >
        <svg viewBox="0 0 100 160" preserveAspectRatio="none" className="absolute inset-[8%_22%] h-[84%] w-[56%]">
          <g fill="none" stroke={night ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.9)'} strokeWidth="2.2" vectorEffect="non-scaling-stroke">
            <rect x="2" y="2" width="96" height="156" />
            <line x1="2" y1="80" x2="98" y2="80" />
            <circle cx="50" cy="80" r="14" />
            <rect x="25" y="2" width="50" height="24" />
            <rect x="25" y="134" width="50" height="24" />
          </g>
        </svg>
      </div>
      {children}
    </div>
  )
}

/* ------------------------------------------------------------------ Pitch drawings (top-down, 400 × 260) */
export type PitchKind = 'football' | 'cricket' | 'court'

export function pitchKindFor(sportIcon: string | undefined): PitchKind {
  if (sportIcon === 'football') return 'football'
  if (sportIcon === 'cricket') return 'cricket'
  return 'court'
}

const HIGHLIGHT = 'color-mix(in oklab, var(--accent) 50%, white)'

function FootballLines({ stroke = '#fff' }: { stroke?: string }) {
  return (
    <g fill="none" stroke={stroke} strokeWidth="2.5">
      <rect x="14" y="14" width="372" height="232" />
      <path d="M200 14V246" />
      <circle cx="200" cy="130" r="34" />
      <rect x="14" y="72" width="54" height="116" />
      <rect x="14" y="104" width="22" height="52" />
      <rect x="332" y="72" width="54" height="116" />
      <rect x="364" y="104" width="22" height="52" />
      <path d="M68 108a26 26 0 0 1 0 44M332 108a26 26 0 0 0 0 44" />
      <path d="M14 22a8 8 0 0 0 8-8M378 14a8 8 0 0 0 8 8M386 238a8 8 0 0 0-8 8M22 246a8 8 0 0 0-8-8" />
    </g>
  )
}

/**
 * Drawing of a ground, used when the owner has not uploaded a photo.
 * `focus` dims the other half (a half pitch); `split` labels both halves.
 */
export function PitchArt({
  kind = 'football',
  focus,
  split,
  divider,
  stretch,
  className,
}: {
  kind?: PitchKind
  focus?: 'left' | 'right'
  split?: [string, string]
  /** Dashed line down the middle (a ground that splits into two halves). */
  divider?: boolean
  /** Fill the box exactly (for perspective backdrops) instead of keeping the pitch's shape. */
  stretch?: boolean
  className?: string
}) {
  const netId = React.useId()
  return (
    <svg viewBox="0 0 400 260" preserveAspectRatio={stretch ? 'none' : undefined} aria-hidden="true" className={cn('block h-auto w-full', className)}>
      {kind === 'football' && (
        <>
          <rect width="400" height="260" fill="#2f8c4f" />
          <g fill="#2a8048">
            {[0, 80, 160, 240, 320].map((x) => (
              <rect key={x} x={x} width="40" height="260" />
            ))}
          </g>
          <FootballLines stroke="rgba(255,255,255,0.92)" />
          <g fill="#fff">
            <circle cx="200" cy="130" r="3" />
            <circle cx="50" cy="130" r="2.5" />
            <circle cx="350" cy="130" r="2.5" />
          </g>
          <g fill="none" stroke="#fff" strokeWidth="2">
            <rect x="6" y="114" width="8" height="32" />
            <rect x="386" y="114" width="8" height="32" />
          </g>
        </>
      )}
      {kind === 'cricket' && (
        <>
          <defs>
            <pattern id={netId} width="9" height="9" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
              <path d="M0 0H9M0 0V9" stroke="#fff" strokeOpacity="0.16" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="400" height="260" fill="#2c8550" />
          <g fill="#287a49">
            {[0, 52, 104, 156, 208].map((y) => (
              <rect key={y} y={y} width="400" height="26" />
            ))}
          </g>
          <rect x="10" y="10" width="380" height="240" fill={`url(#${netId})`} />
          <rect x="10" y="10" width="380" height="240" fill="none" stroke="#fff" strokeOpacity="0.75" strokeWidth="2.5" />
          <ellipse cx="200" cy="130" rx="150" ry="94" fill="none" stroke="#fff" strokeOpacity="0.35" strokeWidth="2" strokeDasharray="6 6" />
          <rect x="108" y="113" width="184" height="34" rx="2" fill="#cdb57f" />
          <path d="M134 103V157M266 103V157" stroke="#fff" strokeWidth="2.5" />
          <g fill="#fff7e2">
            {[117, 121.5, 126, 271.4, 275.9, 280.4].map((x) => (
              <rect key={x} x={x} y="124" width="2.6" height="12" />
            ))}
          </g>
        </>
      )}
      {kind === 'court' && (
        <>
          <rect width="400" height="260" fill="#1f6b4a" />
          <rect x="40" y="30" width="320" height="200" fill="#2a8a5f" />
          <g fill="none" stroke="#fff" strokeOpacity="0.9" strokeWidth="2.5">
            <rect x="40" y="30" width="320" height="200" />
            <path d="M200 30V230M40 130H360" />
            <rect x="110" y="30" width="180" height="200" />
          </g>
        </>
      )}
      {focus && (
        <>
          <rect x={focus === 'left' ? 200 : 0} width="200" height="260" fill="#05130c" opacity="0.62" />
          <rect x={focus === 'left' ? 14 : 200} y="14" width="186" height="232" fill="none" style={{ stroke: HIGHLIGHT }} strokeWidth="5" />
        </>
      )}
      {divider && !split && <path d="M200 6V254" style={{ stroke: HIGHLIGHT }} strokeWidth="4" strokeDasharray="10 8" />}
      {split && (
        <>
          <path d="M200 6V254" style={{ stroke: HIGHLIGHT }} strokeWidth="4" strokeDasharray="10 8" />
          <g fill="#fff" fontFamily="var(--font-display)" fontWeight="800" fontSize="20" textAnchor="middle" letterSpacing="1">
            <text x="107" y="50">{split[0].toUpperCase()}</text>
            <text x="293" y="50">{split[1].toUpperCase()}</text>
          </g>
        </>
      )}
    </svg>
  )
}

/** A glowing pitch in perspective whose lines draw themselves in, with floodlights at the corners (inner-page headers). */
export function PitchSketch({ className }: { className?: string }) {
  const d = (s: number) => ({ '--d': `${s}s` }) as React.CSSProperties
  return (
    <div aria-hidden="true" className={cn('pitch-sketch relative h-[340px]', className)}>
      <div className="pitch-sketch-glow" />
      <div className="pitch-sketch-plane">
        <svg viewBox="0 0 400 260" className="size-full overflow-visible">
          <rect x="14" y="14" width="372" height="232" rx="3" className="ps-fill" />
          <g className="ps-lines" fill="none" stroke="rgb(255 255 255 / 0.75)" strokeWidth="2.2">
            <rect pathLength={1} style={d(0.15)} x="14" y="14" width="372" height="232" rx="3" />
            <path pathLength={1} style={d(0.5)} d="M200 14V246" />
            <circle pathLength={1} style={{ ...d(0.7), stroke: HIGHLIGHT }} cx="200" cy="130" r="34" />
            <rect pathLength={1} style={d(0.85)} x="14" y="72" width="54" height="116" />
            <rect pathLength={1} style={d(0.85)} x="332" y="72" width="54" height="116" />
            <rect pathLength={1} style={d(1)} x="14" y="104" width="22" height="52" />
            <rect pathLength={1} style={d(1)} x="364" y="104" width="22" height="52" />
            <path pathLength={1} style={d(1.1)} d="M68 108a26 26 0 0 1 0 44" />
            <path pathLength={1} style={d(1.1)} d="M332 108a26 26 0 0 0 0 44" />
          </g>
          {[
            [14, 14],
            [386, 14],
            [14, 246],
            [386, 246],
          ].map(([x, y]) => (
            <circle key={`${x}-${y}`} className="ps-lamp" cx={x} cy={y} r="5" />
          ))}
        </svg>
      </div>
    </div>
  )
}

/** Faint pitch markings laid in perspective, for dark hero backgrounds. */
export function PitchLines({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 400 260" preserveAspectRatio="none" aria-hidden="true" className={cn('pointer-events-none origin-bottom [transform:perspective(600px)_rotateX(56deg)]', className)}>
      <FootballLines stroke="currentColor" />
    </svg>
  )
}

/** Decorative street-map pattern behind the address when no map coordinates are set. */
export function MapArt({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 400 260" preserveAspectRatio="xMidYMid slice" aria-hidden="true" className={cn('block', className)}>
      <rect width="400" height="260" fill="#eceee7" />
      <g fill="#e1e4da">
        <rect x="18" y="16" width="100" height="70" rx="8" />
        <rect x="172" y="16" width="76" height="84" rx="8" />
        <rect x="292" y="20" width="94" height="58" rx="8" />
        <rect x="24" y="160" width="90" height="80" rx="8" />
        <rect x="182" y="158" width="62" height="86" rx="8" />
        <rect x="296" y="168" width="96" height="76" rx="8" />
      </g>
      <rect x="290" y="92" width="100" height="22" rx="6" fill="#cfe3d1" />
      <path d="M-10 238C80 214 150 256 240 226S370 196 420 210" fill="none" stroke="#d3e3ec" strokeWidth="12" />
      <g fill="none" strokeLinecap="round">
        <g stroke="#d9dcd2">
          <path d="M-10 132H410" strokeWidth="19" />
          <path d="M150-10V270" strokeWidth="15" />
          <path d="M272-10 254 270" strokeWidth="12" />
          <path d="M-10 58 150 102" strokeWidth="10" />
        </g>
        <g stroke="#fff">
          <path d="M-10 132H410" strokeWidth="16" />
          <path d="M150-10V270" strokeWidth="12" />
          <path d="M272-10 254 270" strokeWidth="9" />
          <path d="M-10 58 150 102" strokeWidth="7" />
        </g>
      </g>
    </svg>
  )
}
